import { of as observableOf, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  TemplateRef,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ProcessService } from '../../../services/process.service';
import { FileToUpload } from '../../../models/file-to-upload.model';
import {
  ProcessParticipation,
  Process,
  ProcessStage,
} from '../../../models/process-models';
import { ConfigService } from '../../../services/config.service';
import { ProcessParticipationService } from '../../../services/process.participation.service';
import { saveAs } from 'file-saver/FileSaver';
import { BsModalRef, BsModalService } from 'ngx-bootstrap/modal';

@Component({
  selector: 'app-process-builder',
  templateUrl: './process-builder.component.html',
  styleUrls: ['./process-builder.component.scss'],
})
export class ProcessBuilderComponent implements OnInit {
  process: Process;
  processId: string;
  fileToUpload: FileToUpload;
  processParticipators: Array<ProcessParticipation>;
  navigateUrlPath = '/admin/processes-list';
  isSpinnerEnable: boolean;
  isOnSaving: boolean = false;
  processStage = ProcessStage;

  modalRef: BsModalRef;

  @ViewChild('fileInput') fileInput: ElementRef;

  constructor(
    private route: ActivatedRoute,
    private processService: ProcessService,
    private router: Router,
    private appData: ConfigService,
    private processParticipationService: ProcessParticipationService,
    private bsModalService: BsModalService
  ) {}

  ngOnInit() {
    this.processId = this.route.snapshot.paramMap.get('id');
    if (this.processId) {
      this.setSpinnerEnabled(true);
      this.processService.getOne(+this.processId).subscribe((p) => {
        this.process = p;
        this.setSpinnerEnabled(false);
      });
      this.processParticipationService
        .getByProcessId(+this.processId)
        .subscribe((p) => (this.processParticipators = p));
    } else {
      this.process = { stage: ProcessStage.Draft };
    }
  }

  removeProcessParticipations(processDurationAppraiseeId: number) {
    this.processParticipators = this.processParticipators.filter(
      (p) => p.processDurationAppraiseeId != processDurationAppraiseeId
    );
  }

  openConfirmationModal(modalTemplate: TemplateRef<any>) {
    this.modalRef = this.bsModalService.show(modalTemplate);
  }

  getFile(event: any) {
    this.modalRef.hide();
    if (!event.target.files || !(event.target.files.length > 0)) {
      return;
    }
    const file: File = event.target.files[0];
    if (!this.getIsExcelFile(file)) {
      return;
    }
    this.uploadFile(file);
  }

  saveProcess(): Observable<any> {
    this.processService.saveEmployeeSettings();
    this.processService.saveButtonClicked();
    if (this.process.id) {
      return this.processService.update(this.process);
    }
    return this.processService.create(this.process).pipe(
      switchMap((process) => {
        if (!this.isEmpty(process)) {
          this.process = process;
          this.processId = this.process.id.toString();
        }
        return observableOf({});
      })
    );
  }

  onSaveClicked() {
    this.isOnSaving = true;
    this.setSpinnerEnabled(true);
    this.saveProcess().subscribe(() => {
      this.isOnSaving = false;
      this.setSpinnerEnabled(false);
    });
  }

  onPickRespondentsClicked() {
    this.setSpinnerEnabled(true);
    this.saveProcess()
      .pipe(
        switchMap(() =>
          this.processParticipationService.getRespondents(this.process)
        )
      )
      .subscribe((result) => {
        this.processParticipators = result;
        this.setSpinnerEnabled(false);
      });
  }

  finalizeProcess() {
    if (this.isOnSaving) {
      return;
    }
    this.isOnSaving = true;
    this.processService.finalizeProcess(this.process).subscribe((x) => {
      this.isOnSaving = false;
      if (x != null) {
        this.modalRef.hide();
        return;
      }
      this.router.navigate(['/admin/processes-list']);
      this.modalRef.hide();
    });
  }

  downloadFile() {
    this.processParticipationService
      .exportOne(this.process.id)
      .subscribe((responce) => {
        const filename = 'Template' + new Date().toISOString() + '.xlsx';
        saveAs(responce, filename);
      });
  }

  setSpinnerEnabled(isEnabled: boolean) {
    this.isSpinnerEnable = isEnabled;
  }

  getProcessName() {
    return this.process && this.process.id
      ? [...this.process.name].shift().name
      : 'New process';
  }

  private uploadFile(file: File) {
    this.setSpinnerEnabled(true);
    this.fileToUpload = { id: this.process.id, file: file, name: file.name };
    this.processParticipationService.updateByFile(this.fileToUpload).subscribe(
      (x) => {
        this.processParticipators = x;
        this.setSpinnerEnabled(false);
      },
      (error) => {
        this.setSpinnerEnabled(false);
      }
    );
  }

  private getIsExcelFile(file: File): boolean {
    return this.appData.configData.excelMIMETypes.some((x) => x === file.type);
  }

  private isEmpty(source: any) {
    return JSON.stringify(source) === '{}';
  }
}
